import { RegistrationPage } from "@/features/resident/RegistrationPage";
import { LoginPage } from "@/features/auth/LoginPage";
import type { ForwardRefExoticComponent, ReactElement, RefAttributes } from "react";
import { routes } from "./paths";
import App from "../App";
import { BookAlert, LayoutDashboard, LucideUserCheck, UserCircle, type LucideProps } from "lucide-react";
import { DashboardPage } from "@/features/dashboard/dashboard";
import { AccountPage } from "@/features/account";
import { DissertationPage } from "@/features/dissertation/dissertation-page";
import { DissertationDetailsPage } from "@/features/dissertation/details";
import { PasswordResetPage } from "@/features/auth/PasswordResetPage";
import { ExaminerScoreSheet } from "@/features/examiner/scoresheet";
import ExaminerFeedback from "@/features/examiner/feedback";
import { EventRegistrationPage } from "@/features/events/EventRegistrationPage";
import { PaymentCallbackPage } from "@/features/events/PaymentCallbackPage";

/**
 * RouteType defines the type of route for sidebar rendering and organization.
 * - "normal": A standard route that appears as a single item in the sidebar.
 * - "dropdown": A route that contains sub-routes, displayed as a dropdown in the sidebar.
 * - "title": A non-clickable title used to group related routes in the sidebar.
 * - "divider": A visual separator used to divide sections in the sidebar.
 * - "index": A special route that serves as the default or home route.
 * - "dashboard": Nothing special, just indicate it shouldn't show in dashboard links
 */
type RouteType = "normal" | "dropdown" | "title" | "divider" | "index" | "dashboard";

export interface IRoute {
	[index: string]:
	| string
	| undefined
	| ForwardRefExoticComponent<Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>>
	| ReactElement
	| null
	| string[]
	| IRoute[]
	| IRoute;
	type: RouteType;
	name: string;
	key?: string;
	icon?: ForwardRefExoticComponent<Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>> | null;
	path?: string;
	className?: string;
	component?: ReactElement;
	permissions?: string[];
	children?: IRoute[];
	description?: string;
}

export const dashboardPath = "/app";

const pageRoutes: Array<IRoute> = [
	{
		type: "index",
		name: "index",
		path: routes.BASE_URL,
		component: <App />,
	},
	{
		type: "index",
		name: "Log In",
		key: "login",
		path: routes.LOGIN_PATH,
		component: <LoginPage />,
	},
	{
		type: "index",
		name: "Password Reset",
		key: "password-reset",
		path: routes.PASSWORD_RESET_PATH,
		component: <PasswordResetPage />,
	},
	{
		type: "index",
		name: 'Examiner Score Sheet',
		key: "examiner-scoresheet",
		path: routes.EXAMINER_SCORESHEET_PATH,
		component: <ExaminerScoreSheet />,
	},
	{
		type: "index",
		name: 'Examiner Feedback',
		key: "examiner-feedback",
		path: routes.EXAMINER_FEEDBACK_PATH,
		component: <ExaminerFeedback />,
	},
	{
		type: "index",
		name: 'Event Registration',
		key: "event-registration",
		path: routes.EVENT_REGISTRATION_PATH,
		component: <EventRegistrationPage />,
	},
	{
		type: "index",
		name: 'Event Payment Callback',
		key: "event-payment-callback",
		path: routes.EVENT_PAYMENT_CALLBACK_PATH,
		component: <PaymentCallbackPage />,
	},
	{
		type: "dashboard",
		name: "Dashboard",
		key: "dashboard",
		icon: LayoutDashboard,
		path: routes.BASE_APP_URL,
		component: <DashboardPage />,
	},
	{
		type: "normal",
		name: "Annual Registration",
		key: "registration",
		icon: LucideUserCheck,
		path: routes.REGISTRATION_PATH,
		component: <RegistrationPage />,
		description: " Register residents for the current year. Register residents for the current year.",
	},
	{
		type: "normal",
		name: "Dissertation",
		key: "dissertation",
		icon: BookAlert,
		path: routes.DISSERTATION_SUBMISSIONS_PATH,
		component: <DissertationPage />,
		description: "Submit and manage your dissertation."
	},
	{
		type: "index",
		name: "Dissertation Details",
		key: "dissertation-details",
		icon: BookAlert,
		path: routes.DISSERTATION_DETAILS_PATH,
		component: <DissertationDetailsPage />,
		description: "Submit and manage your dissertation."
	},
	{
		type: "index",
		name: "Manage Account",
		key: "manage-account",
		icon: UserCircle,
		path: routes.ACCOUNT_PAGE_PATH,
		component: <AccountPage />,
		description: "Manage your personal information and security settings",
	}
];

export function getPageLink(page: string): string {
	const link = pageRoutes.flatMap((route: IRoute) => {
		if (route.name.toLowerCase() === page.toLowerCase()) {
			return route.path || "";
		} else if (route.children && route.children.length > 0) {
			return route.children.map((subRoute: IRoute) => {
				if (subRoute.name.toLowerCase() === page.toLowerCase()) {
					return subRoute.path || "";
				}
			});
		}
	});

	return link.join("");
}

export default pageRoutes;